import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingFallback from "../../../components/elements/LoadingFallback";
import { BASE_BOOK_PRICE } from "../../../config/pricing";
import { selectBooksLoadedAt, updateBook } from "../../../redux/slice/profileSlice";
import { getPdfOffer } from "../../../services/bookService";
import { getShopifyCart, loadCartBookIds, syncLocalCartToDB } from "../../../services/shopifyCartService";

/**
 * PDF downsell link in Shopify's abandoned-checkout email (/pdf-offer). The API switches the
 * books from the customer's last checkout to PDF only; this page puts them in the order and
 * opens Review order, where each PDF line already offers "Add the printed book". Nothing to
 * offer (finished the order since, clicked twice) opens My Books.
 *
 * Signed out: UserRoute sends them to the store's /pages/pdf-offer (a theme page), which
 * opens Shopify's sign-in and hands back here with destination=pdfOffer. It also remembers
 * the visit (`rememberPdfOffer`), so a sign-in that lands on /dashboard still comes back here.
 */

const PENDING_KEY = "pdfOfferPending";
const PENDING_MAX_AGE_MS = 60 * 60 * 1000;

/** The theme page that signs a signed-out customer in and hands them back to /pdf-offer. */
export const pdfOfferStorePage = () => {
  try {
    return new URL("/pages/pdf-offer", process.env.REACT_APP_SHOPIFY_URL?.trim()).href;
  } catch {
    return null;
  }
};

export const rememberPdfOffer = () => {
  try {
    localStorage.setItem(PENDING_KEY, String(Date.now()));
  } catch {
    /* storage unavailable: they land on My Books after signing in */
  }
};

/**
 * True while a signed-out visit to /pdf-offer is waiting for this sign-in. Read, not taken:
 * UserLayout's step redirect checks it too, and fires again after the smart redirect has
 * already left /dashboard. The offer page clears it.
 */
export const hasPendingPdfOffer = () => {
  try {
    const at = Number(localStorage.getItem(PENDING_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < PENDING_MAX_AGE_MS;
  } catch {
    return false;
  }
};

const clearPendingPdfOffer = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* nothing to clear */
  }
};

// The printed book's product, as My Books adds it. A PDF-only book checks out as the PDF
// product whatever the line says (ShopifyCart), so the line only needs the book.
const BASE_VARIANT_ID = "gid://shopify/ProductVariant/47524057547000";
const BASE_PRODUCT_ID = "gid://shopify/Product/9314844639480";

const newCartLine = (bookId, book) => ({
  id: `book-${bookId}-${BASE_VARIANT_ID}`,
  bookId,
  title: book?.title || "Custom Coloring Book",
  name: book?.title || "Custom Coloring Book",
  quantity: 1,
  shopifyVariantId: BASE_VARIANT_ID,
  shopifyProductId: BASE_PRODUCT_ID,
  planPricingData: { price: BASE_BOOK_PRICE, unitPrice: BASE_BOOK_PRICE, quantity: 1, priceDescription: "Select Plan", currencyCode: "USD" },
  recipient: book?.recipientName || "",
  version: book?.version || 1,
  previewUrl: book?.previewUrl || book?.coverImageThumbnail || book?.coverImage,
  coverImage: book?.coverImageThumbnail || book?.coverImage || book?.previewUrl,
  createdAt: new Date().toISOString(),
});

const readLocalCart = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("selectedPlan") || "[]");
    return (Array.isArray(parsed) ? parsed : [parsed]).filter(Boolean);
  } catch {
    return [];
  }
};

/**
 * Make sure every offered book has a line in this browser's cart, which is what Review order
 * shows. A browser with no cart takes the server's first; a book still missing comes from the
 * server cart, or a new line when it was removed after checkout. A PDF book has no $5 add-on.
 */
const putBooksInCart = async (bookIds, myBooks) => {
  await loadCartBookIds();
  const cart = readLocalCart();
  const inCart = (id) => cart.some((line) => String(line.bookId) === id);
  const missing = bookIds.filter((id) => !inCart(id));
  if (missing.length) {
    const res = await getShopifyCart();
    const rows = Array.isArray(res?.data) ? res.data : [];
    missing.forEach((id) => {
      const row = rows.find((r) => String(r.bookId) === id);
      cart.push(row || newCartLine(id, myBooks.find(({ _id }) => String(_id) === id)));
    });
  }
  const next = cart.map((line) => (bookIds.includes(String(line.bookId)) ? { ...line, includePdf: false } : line));
  localStorage.setItem("selectedPlan", JSON.stringify(next));
  window.dispatchEvent(new Event("cartUpdated"));
  await syncLocalCartToDB();
};

const PdfOffer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const booksLoadedAt = useSelector(selectBooksLoadedAt);
  const myBooks = useSelector((state) => state?.profile?.myBooks) || [];
  const started = useRef(false);

  useEffect(clearPendingPdfOffer, []);

  // Wait for the book list, so a list still loading can't bring back the printed format.
  useEffect(() => {
    if (!booksLoadedAt || started.current) return;
    started.current = true;
    (async () => {
      const res = await getPdfOffer();
      const bookIds = (res?.data?.bookIds || []).map(String);
      if (!res?.success || !bookIds.length) {
        navigate("/dashboard", { replace: true });
        return;
      }
      bookIds.forEach((id) => dispatch(updateBook({ _id: id, tag: "downloadPdf" })));
      try {
        await putBooksInCart(bookIds, myBooks);
      } catch (err) {
        console.error("PDF offer: cart update failed", err);
      }
      navigate("/finalize-order", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booksLoadedAt]);

  return <LoadingFallback />;
};

export default PdfOffer;
